import React, { forwardRef } from 'react';
import FlightPath from './FlightPath';
import FlightQRCode from './FlightQRCode';
import { getRarityInfo, formatTime } from '../utils';
import { getAirport } from '../constants';

const TicketPreview = forwardRef(({ flightData }, ref) => {
  const rarityInfo = getRarityInfo(flightData.classOfService);
  const depAirport = getAirport(flightData.departure);
  const arrAirport = getAirport(flightData.arrival);

  // United's official blue
  const unitedBlue = '#0033A0';
  const accentBlue = '#0066FF';

  return (
    <div
      ref={ref}
      style={{
        width: '600px',
        minHeight: '380px',
        background: 'linear-gradient(145deg, #0a0a0a 0%, #1a1a2e 50%, #0a0a0a 100%)',
        borderRadius: '16px',
        padding: '0',
        position: 'relative',
        fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif",
        boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
      }}
    >
      {/* Noise texture overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.03,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        pointerEvents: 'none',
      }} />

      {/* Top accent line */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `linear-gradient(90deg, transparent 0%, ${accentBlue} 50%, transparent 100%)`,
      }} />

      {/* Rarity indicator glow */}
      <div style={{
        position: 'absolute',
        top: '-50px',
        right: '-50px',
        width: '200px',
        height: '200px',
        background: `radial-gradient(circle, ${rarityInfo.color}22 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Main content */}
      <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '24px 28px 16px',
        }}>
          {/* United Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${unitedBlue} 0%, #004494 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 20px ${unitedBlue}44`,
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" opacity="0.9"/>
                <path d="M12 2 C12 2 16 8 16 12 C16 16 12 22 12 22 C12 22 8 16 8 12 C8 8 12 2 12 2" stroke="white" strokeWidth="1.5" fill="none"/>
                <ellipse cx="12" cy="12" rx="10" ry="4" stroke="white" strokeWidth="1.5" fill="none"/>
              </svg>
            </div>
            <div>
              <div style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#ffffff',
                letterSpacing: '2px',
              }}>
                UNITED
              </div>
              <div style={{
                fontSize: '9px',
                color: 'rgba(255,255,255,0.4)',
                letterSpacing: '1px',
                marginTop: '2px',
              }}>
                FLIGHT SEGMENT NFT
              </div>
            </div>
          </div>

          {/* Flight number & Class */}
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#ffffff',
              letterSpacing: '1px',
            }}>
              {flightData.flightNumber}
            </div>
            <div style={{
              display: 'inline-block',
              padding: '3px 10px',
              borderRadius: '12px',
              background: `${rarityInfo.color}22`,
              border: `1px solid ${rarityInfo.color}44`,
              marginTop: '4px',
            }}>
              <span style={{
                fontSize: '9px',
                fontWeight: '600',
                color: rarityInfo.color,
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}>
                {flightData.classOfService}
              </span>
            </div>
          </div>
        </div>

        {/* Flight Path Visualization */}
        <div style={{
          flex: 1,
          padding: '0 28px',
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            padding: '0 28px',
          }}>
            <FlightPath accentColor={accentBlue} />
          </div>

          {/* Airport codes overlay */}
          <div style={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            padding: '0 20px',
          }}>
            {/* Departure */}
            <div>
              <div style={{
                fontSize: '48px',
                fontWeight: '700',
                color: '#ffffff',
                letterSpacing: '-1px',
                lineHeight: 1,
                textShadow: '0 0 40px rgba(0,0,0,0.8)',
              }}>
                {flightData.departure}
              </div>
              <div style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.5)',
                marginTop: '4px',
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {depAirport.city}
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: accentBlue,
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                {formatTime(flightData.departureTime)}
                {flightData.departureGate && (
                  <span style={{
                    fontSize: '11px',
                    color: 'rgba(255,255,255,0.5)',
                    fontWeight: '500',
                  }}>
                    Gate {flightData.departureGate}
                  </span>
                )}
              </div>
            </div>

            {/* Duration badge */}
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              backdropFilter: 'blur(10px)',
              padding: '8px 16px',
              borderRadius: '20px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#ffffff',
              }}>
                {flightData.duration}
              </div>
            </div>

            {/* Arrival */}
            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontSize: '48px',
                fontWeight: '700',
                color: '#ffffff',
                letterSpacing: '-1px',
                lineHeight: 1,
                textShadow: '0 0 40px rgba(0,0,0,0.8)',
              }}>
                {flightData.arrival}
              </div>
              <div style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.5)',
                marginTop: '4px',
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                marginLeft: 'auto',
              }}>
                {arrAirport.city}
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: accentBlue,
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '8px',
              }}>
                {flightData.arrivalGate && (
                  <span style={{
                    fontSize: '11px',
                    color: 'rgba(255,255,255,0.5)',
                    fontWeight: '500',
                  }}>
                    Gate {flightData.arrivalGate}
                  </span>
                )}
                {formatTime(flightData.arrivalTime)}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom info bar */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          padding: '16px 28px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          {/* Flight details */}
          <div style={{ display: 'flex', gap: '32px' }}>
            <div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '4px' }}>
                DATE
              </div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff' }}>
                {flightData.date}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '4px' }}>
                AIRCRAFT
              </div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff' }}>
                {flightData.aircraft}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '4px' }}>
                SEAT
              </div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff' }}>
                {flightData.seat}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '4px' }}>
                TAIL
              </div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff' }}>
                {flightData.tailNumber}
              </div>
            </div>
          </div>

          {/* QR Code */}
          <FlightQRCode flightData={flightData} size={56} />
        </div>

        {/* Crew section - only if data exists */}
        {(flightData.captain || flightData.firstOfficer) && (
          <div style={{
            background: 'rgba(255,255,255,0.02)',
            borderTop: '1px solid rgba(255,255,255,0.05)',
            padding: '12px 28px',
            display: 'flex',
            gap: '24px',
          }}>
            {flightData.captain && (
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                <span style={{ color: 'rgba(255,255,255,0.3)' }}>CPT</span> {flightData.captain}
              </div>
            )}
            {flightData.firstOfficer && (
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                <span style={{ color: 'rgba(255,255,255,0.3)' }}>FO</span> {flightData.firstOfficer}
              </div>
            )}
          </div>
        )}

        {/* Lounge badges - only if any lounge is selected */}
        {(flightData.lounges?.departurePolarisLounge || 
          flightData.lounges?.departureUnitedClub || 
          flightData.lounges?.arrivalPolarisLounge || 
          flightData.lounges?.arrivalUnitedClub) && (
          <div style={{
            background: 'rgba(255,255,255,0.02)',
            borderTop: '1px solid rgba(255,255,255,0.05)',
            padding: '10px 28px',
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
          }}>
            {flightData.lounges?.departurePolarisLounge && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0033A0 0%, #004494 100%)',
                boxShadow: '0 2px 8px rgba(0, 51, 160, 0.3)',
              }}>
                <span style={{ fontSize: '10px' }}>✨</span>
                <span style={{ fontSize: '9px', fontWeight: '600', color: '#ffffff', letterSpacing: '0.5px' }}>
                  POLARIS @ {flightData.departure}
                </span>
              </div>
            )}
            {flightData.lounges?.departureUnitedClub && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)',
                boxShadow: '0 2px 8px rgba(0, 102, 255, 0.3)',
              }}>
                <span style={{ fontSize: '10px' }}>🎫</span>
                <span style={{ fontSize: '9px', fontWeight: '600', color: '#ffffff', letterSpacing: '0.5px' }}>
                  CLUB @ {flightData.departure}
                </span>
              </div>
            )}
            {flightData.lounges?.arrivalPolarisLounge && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0033A0 0%, #004494 100%)',
                boxShadow: '0 2px 8px rgba(0, 51, 160, 0.3)',
              }}>
                <span style={{ fontSize: '10px' }}>✨</span>
                <span style={{ fontSize: '9px', fontWeight: '600', color: '#ffffff', letterSpacing: '0.5px' }}>
                  POLARIS @ {flightData.arrival}
                </span>
              </div>
            )}
            {flightData.lounges?.arrivalUnitedClub && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)',
                boxShadow: '0 2px 8px rgba(0, 102, 255, 0.3)',
              }}>
                <span style={{ fontSize: '10px' }}>🎫</span>
                <span style={{ fontSize: '9px', fontWeight: '600', color: '#ffffff', letterSpacing: '0.5px' }}>
                  CLUB @ {flightData.arrival}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Corner accent */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100px',
        height: '3px',
        background: `linear-gradient(90deg, ${unitedBlue} 0%, transparent 100%)`,
      }} />
    </div>
  );
});

TicketPreview.displayName = 'TicketPreview';

export default TicketPreview;
